import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import DocumentChatShell from '../shells/DocumentChatShell';
import AssistantBotRow from '../shells/AssistantBotRow';
import { useDocumentChatNavigation } from '../shells/useDocumentChatNavigation';
import ChatSaveErrorBanner from '../components/ChatSaveErrorBanner';
import ContinueButtonBar from '../components/ContinueButtonBar';
import DocumentChatLayout from '../layout/DocumentChatLayout';
import { interpolateI18n } from '../utils/interpolateI18n';
import { useChatThreadScroll } from '../hooks/useChatThreadScroll';
import { useStaggeredPreviewCta } from '../hooks/useStaggeredPreviewCta';
import HealthInfoZuidoChatComposer, {
  HealthInfoZuidoDeclinedComposer,
} from './HealthInfoZuidoChatComposer';
import HealthInfoZuidoChatThread from './HealthInfoZuidoChatThread';
import { conditionsSummaryText, workHistorySummaryText } from './healthInfoZuidoSteps';
import { useHealthInfoZuidoFlow } from './useHealthInfoZuidoFlow';

const FIRST_ASSISTANT_DELAY_MS = 1000;
const FOLLOW_ASSISTANT_GAP_MS = 650;

export interface HealthInfoZuidoChatFlowProps {
  documentTitle: string;
  currentEmail?: string | null;
  invitationId?: string | null;
  onBack: () => void;
  onOpenConsentPdfRead?: () => Promise<void>;
  onOpenWorkHistoryFormPdf?: () => Promise<void>;
  /** Forms 3.1+3.2 agreed — open filled preview before form 4. */
  onPauseContinue?: () => void | Promise<void>;
  /** Declined on form 3.1 or 3.2 — record and return to checklist. */
  onDeclinedContinue?: () => void | Promise<void>;
  /** Form 4 work history confirmed or correction recorded — complete FF011. */
  onWorkHistoryCompleteContinue?: () => void | Promise<void>;
  /** Called when returning from forms 3.1/3.2 filled preview. */
  resumeAfterForms312Preview?: boolean;
  /** Parent resets resume flag after handling. */
  onForms312PreviewResumed?: () => void;
}

const HealthInfoZuidoChatFlow: React.FC<HealthInfoZuidoChatFlowProps> = ({
  documentTitle,
  currentEmail,
  invitationId,
  onBack,
  onOpenConsentPdfRead,
  onOpenWorkHistoryFormPdf,
  onPauseContinue,
  onDeclinedContinue,
  onWorkHistoryCompleteContinue,
  resumeAfterForms312Preview,
  onForms312PreviewResumed,
}) => {
  const { t } = useLanguage();
  const { exitToChecklist } = useDocumentChatNavigation(onBack);
  const flow = useHealthInfoZuidoFlow(currentEmail, invitationId, t);

  const [consentRevealPhase, setConsentRevealPhase] = useState(0);
  const [workHistoryRevealPhase, setWorkHistoryRevealPhase] = useState(0);
  const threadEndRef = useRef<HTMLDivElement | null>(null);
  const resumeHandledRef = useRef(false);

  useEffect(() => {
    resumeHandledRef.current = false;
  }, [documentTitle, invitationId]);

  const greeting = useMemo(
    () => interpolateI18n(t('submitDocuments.healthInfoZuido.greetingPrefix'), { documentTitle }),
    [t, documentTitle]
  );

  const conditionsSummary = useMemo(() => conditionsSummaryText(t), [t]);
  const workHistorySummary = useMemo(() => workHistorySummaryText(t), [t]);

  const { showIntro: showPauseCta, showCta: showPauseCtaButton } = useStaggeredPreviewCta(flow.forms312Complete);
  const { showCta: showWorkHistoryCompleteCta } = useStaggeredPreviewCta(flow.workHistoryComplete);

  useEffect(() => {
    if (flow.isLoading || flow.chatPhase !== 'consent') return;
    setConsentRevealPhase(0);
    const t1 = window.setTimeout(() => setConsentRevealPhase(1), FIRST_ASSISTANT_DELAY_MS);
    const t2 = window.setTimeout(() => setConsentRevealPhase(2), FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS);
    const t3 = window.setTimeout(
      () => setConsentRevealPhase(3),
      FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS * 2
    );
    const t4 = window.setTimeout(
      () => setConsentRevealPhase(4),
      FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS * 3
    );
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
      window.clearTimeout(t4);
    };
  }, [documentTitle, flow.isLoading, flow.chatPhase]);

  useEffect(() => {
    if (flow.isLoading || flow.chatPhase !== 'workHistory') return;
    setWorkHistoryRevealPhase(0);
    const t1 = window.setTimeout(() => setWorkHistoryRevealPhase(1), FIRST_ASSISTANT_DELAY_MS);
    const t2 = window.setTimeout(() => setWorkHistoryRevealPhase(2), FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS);
    const t3 = window.setTimeout(
      () => setWorkHistoryRevealPhase(3),
      FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS * 2
    );
    const t4 = window.setTimeout(
      () => setWorkHistoryRevealPhase(4),
      FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS * 3
    );
    const t5 = window.setTimeout(
      () => setWorkHistoryRevealPhase(5),
      FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS * 4
    );
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
      window.clearTimeout(t4);
      window.clearTimeout(t5);
    };
  }, [flow.isLoading, flow.chatPhase]);

  useEffect(() => {
    if (!resumeAfterForms312Preview || resumeHandledRef.current || flow.isLoading) return;
    resumeHandledRef.current = true;
    void flow.advanceAfterForms312Preview().then(() => {
      onForms312PreviewResumed?.();
    });
  }, [resumeAfterForms312Preview, flow.isLoading, flow.advanceAfterForms312Preview, onForms312PreviewResumed]);

  useChatThreadScroll(threadEndRef, [
    consentRevealPhase,
    workHistoryRevealPhase,
    flow.committed.length,
    flow.forms312Complete,
    flow.workHistoryComplete,
    flow.formDeclined,
    flow.assistantQuestionVisible,
    flow.chatPhase,
    showPauseCta,
    showWorkHistoryCompleteCta,
  ]);

  const consentCanShowThread = consentRevealPhase >= 4 && !flow.isLoading && flow.chatPhase === 'consent';
  const workHistoryCanShowThread =
    (workHistoryRevealPhase >= 5 || flow.values.workHistoryIntroContinued === 'true') &&
    !flow.isLoading &&
    flow.chatPhase === 'workHistory';

  const showIntroComposer =
    flow.currentStep?.stepId === 'intro' && !flow.isLoading && consentRevealPhase >= 4 && flow.chatPhase === 'consent';

  const showWorkHistoryIntroComposer =
    flow.currentStep?.stepId === 'workHistoryIntro' &&
    !flow.isLoading &&
    workHistoryRevealPhase >= 5 &&
    flow.chatPhase === 'workHistory';

  const showChoiceComposer =
    (consentCanShowThread || workHistoryCanShowThread) &&
    !flow.forms312Complete &&
    !flow.workHistoryComplete &&
    !flow.formDeclined &&
    (flow.currentStep?.stepId === 'agreeForm31' ||
      flow.currentStep?.stepId === 'agreeForm32' ||
      flow.currentStep?.stepId === 'confirmWorkHistory' ||
      flow.currentStep?.stepId === 'workHistoryCorrection');

  return (
    <DocumentChatShell onBack={exitToChecklist} cancelLabel={t('submitDocuments.chat.cancelProcedure')}>
      <DocumentChatLayout
        thread={
          <>
            {flow.chatPhase === 'consent' && (
              <>
                {consentRevealPhase >= 1 && <AssistantBotRow animateIn>{greeting}</AssistantBotRow>}
                {consentRevealPhase >= 2 && (
                  <AssistantBotRow animateIn>{t('submitDocuments.healthInfoZuido.intro')}</AssistantBotRow>
                )}
                {consentRevealPhase >= 3 && <AssistantBotRow animateIn>{conditionsSummary}</AssistantBotRow>}
              </>
            )}
            {flow.chatPhase === 'workHistory' && (
              <>
                {workHistoryRevealPhase >= 1 && (
                  <AssistantBotRow animateIn>
                    {t('submitDocuments.healthInfoZuido.workHistory.greeting')}
                  </AssistantBotRow>
                )}
                {workHistoryRevealPhase >= 2 && (
                  <AssistantBotRow animateIn>{t('submitDocuments.healthInfoZuido.workHistory.intro')}</AssistantBotRow>
                )}
                {workHistoryRevealPhase >= 3 && (
                  <AssistantBotRow animateIn>{workHistorySummary}</AssistantBotRow>
                )}
                {workHistoryRevealPhase >= 4 && (
                  <AssistantBotRow animateIn>
                    {t('submitDocuments.healthInfoZuido.workHistory.prepared')}
                  </AssistantBotRow>
                )}
                {workHistoryRevealPhase >= 5 && (
                  <AssistantBotRow animateIn>
                    {t('submitDocuments.healthInfoZuido.workHistory.reviewPrompt')}
                  </AssistantBotRow>
                )}
              </>
            )}
            {flow.isLoading ? (
              (flow.chatPhase === 'consent' ? consentRevealPhase >= 2 : workHistoryRevealPhase >= 2) && (
                <AssistantBotRow animateIn>{t('submitDocuments.healthInfoZuido.loading')}</AssistantBotRow>
              )
            ) : consentCanShowThread || workHistoryCanShowThread ? (
              <HealthInfoZuidoChatThread flow={flow} />
            ) : null}
            <div ref={threadEndRef} aria-hidden />
            <div ref={flow.threadEndRef} aria-hidden />
          </>
        }
        composer={
          <>
            <ChatSaveErrorBanner message={flow.saveError} />
            {flow.formDeclined && onDeclinedContinue && (consentCanShowThread || flow.formDeclined) && (
              <HealthInfoZuidoDeclinedComposer onContinue={() => void onDeclinedContinue()} />
            )}
            {flow.forms312Complete && showPauseCtaButton && onPauseContinue && (
              <ContinueButtonBar
                label={t('submitDocuments.chat.continue')}
                onContinue={() => void onPauseContinue()}
              />
            )}
            {flow.workHistoryComplete && showWorkHistoryCompleteCta && onWorkHistoryCompleteContinue && (
              <ContinueButtonBar
                label={t('submitDocuments.chat.continue')}
                onContinue={() => void onWorkHistoryCompleteContinue()}
              />
            )}
            {!flow.isLoading && (showIntroComposer || showWorkHistoryIntroComposer || showChoiceComposer) && (
              <HealthInfoZuidoChatComposer
                flow={flow}
                onOpenConsentPdfRead={onOpenConsentPdfRead}
                onOpenWorkHistoryFormPdf={onOpenWorkHistoryFormPdf}
              />
            )}
          </>
        }
      />
    </DocumentChatShell>
  );
};

export default HealthInfoZuidoChatFlow;
